





import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CloseIcon from '@mui/icons-material/Close';
import ThreeDRotationIcon from '@mui/icons-material/ThreeDRotation';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SaveIcon from '@mui/icons-material/Save';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { getAdminProductModelData } from '../../../actions/officeAction';
import { updatedProduct } from '../../../actions/api/product';
import { getProducts } from '../../../actions/product.action';
import { getBlankPriceCatalogs, getPricingPolicy } from '../../CatalogReview/catalogReviewApi';
import { getSocket } from '../../ChatPage/socket';
import {
    publishAdminRealtimeSemanticState,
    subscribeAdminRealtimeSemanticState,
} from '../../Presence/adminRealtimeSemanticState';
import AdminProduct3DStage from './AdminProduct3DStage';
import AdminProduct3DInfoEditor from './AdminProduct3DInfoEditor';
import AdminGraniteTypeSwitcher from './AdminGraniteTypeSwitcher';
import {
    getCategoryTitle,
    getCategoryValue,
    getInitialForm,
    getMillingDimensionsFromPreview,
    getPreviewDimensions,
    getProductCategoryValue,
    getProductName,
    getSceneDimensionsFromMillingForm,
    isDefaultMillingDimensions,
    isSamePayload,
    buildPayload,
    mergeTags,
    formatMillingDimensions,
    fmtMoney,
    normalizeMillingPricing,
    normalizeProductPricing,
    normalizeWorkOption,
    mergeSharedProductSizeRules,
    syncWorkPricingToSharedSizes,
    calcMillingPrice,
    applyMaterialMillingMarkup,
    calcProductPrice,
    calcProductCustomerPrices,
    resolveBlankDimensions,
    validateAdminProduct3DForm,
} from './adminProduct3DPreview.utils';
import { getUnpolishedSurfaceCatalog } from './graniteSurfaceFinish.utils';
import {
    loadAdminProduct3DSceneSettings,
    normalizeAdminProduct3DSceneSettings,
    saveAdminProduct3DSceneSettings,
} from './adminProduct3DSceneSettings';

const isSameMillingDimensions = (a, b) => {
    if (!a || !b) return false;

    return (
        Math.round(Number(a.height || 0)) === Math.round(Number(b.height || 0)) &&
        Math.round(Number(a.width || 0)) === Math.round(Number(b.width || 0)) &&
        Math.round(Number(a.thickness || 0)) === Math.round(Number(b.thickness || 0))
    );
};

const normalizeVariantTextureBinding = (binding = {}) => {
    if (binding?.mode === 'manual') {
        return {
            mode: 'manual',
            applyToAll: binding.applyToAll !== false,
            materialIndexes: Array.from(new Set(
                (Array.isArray(binding.materialIndexes) ? binding.materialIndexes : [])
                    .map((value) => Math.max(0, Math.floor(Number(value) || 0)))
            )).sort((left, right) => left - right),
        };
    }

    return {
        mode: 'auto',
        applyToAll: true,
        materialIndexes: [],
    };
};

const applyActiveMaterialSelectionToForm = (sourceForm, materialKey) => {
    const key = String(materialKey || '');
    if (!key || !sourceForm || typeof sourceForm !== 'object') return sourceForm;
    const variants = Array.isArray(sourceForm.materialPricingVariants)
        ? sourceForm.materialPricingVariants
        : [];
    const selected = variants.find((variant) => String(variant?.materialKey || '') === key);

    /*
     * Realtime может переключить породу, которой ещё нет в локальном
     * materialPricingVariants второго устройства. Для визуального выбора
     * достаточно semantic materialKey: activeMaterialCatalog возьмёт
     * texturePreset из общего blankPriceCatalogs, а textureBinding без
     * локального варианта безопасно останется auto.
     *
     * Pricing-вариант здесь не создаём и бизнес-данные не мутируем.
     */
    if (!selected) {
        if (String(sourceForm.activeMaterialKey || '') === key) return sourceForm;

        return {
            ...sourceForm,
            activeMaterialKey: key,
        };
    }

    if (String(sourceForm.activeMaterialKey || '') === key) return sourceForm;

    return {
        ...sourceForm,
        activeMaterialKey: key,
        productPricing: selected.productPricing ?? sourceForm.productPricing,
        millingPricing: selected.millingPricing ?? sourceForm.millingPricing,
    };
};


const normalizeViewerDimensions = (dimensions = {}) => {
    const height = Number(dimensions?.height || 0);
    const width = Number(dimensions?.width || 0);
    const thickness = Number(dimensions?.thickness || 0);

    if (!(height > 0) || !(width > 0) || !(thickness > 0)) {
        return null;
    }

    return {
        height,
        width,
        thickness,
        unit: 'mm',
    };
};

const viewerDimensionsToScene = (
    dimensions,
    axisPreset = 'vertical',
    source = ''
) => {
    const normalized = normalizeViewerDimensions(dimensions);
    if (!normalized) return null;

    const horizontal = axisPreset === 'horizontal';

    return {
        x: normalized.width / 1000,
        y: horizontal
            ? normalized.thickness / 1000
            : normalized.height / 1000,
        z: horizontal
            ? normalized.height / 1000
            : normalized.thickness / 1000,
        scaleBase: {
            x: normalized.width / 1000,
            y: horizontal
                ? normalized.thickness / 1000
                : normalized.height / 1000,
            z: horizontal
                ? normalized.height / 1000
                : normalized.thickness / 1000,
        },
        axisPreset: horizontal ? 'horizontal' : 'vertical',
        source,
    };
};

const formatViewerDimensions = (dimensions) => {
    const normalized = normalizeViewerDimensions(dimensions);

    if (!normalized) return 'Размер не указан';

    return `${Math.round(normalized.height)}×${Math.round(normalized.width)}×${Math.round(normalized.thickness)} мм`;
};

const getBoolText = (value) => {
    return value ? 'включено' : 'выключено';
};

const getMaterialSelectionModeText = (value) => (
    value === 'granite'
        ? 'Гранит — один выбор породы'
        : 'Стандарт — цвет/текстура материалов'
);

const getFormulaText = (value) => {
    if (value === 'fixed') return 'фиксированная цена';
    if (value === 'area') return 'по площади';
    if (value === 'perimeter') return 'по периметру';
    if (value === 'mixed') return 'смешанная формула';
    if (value === 'volumeForward') return 'по кубатуре: только вверх';
    if (value === 'blankPlusMilling') return 'заготовка + работы + финансовая политика';
    if (value === 'manual') return 'ручная итоговая цена';

    return value || '—';
};

const getAxisText = (value) => {
    if (value === 'vertical') return 'вертикальный памятник';
    if (value === 'horizontal') return 'лежачая плита';

    return 'авто';
};

const getTagsText = (tags) => {
    if (!Array.isArray(tags) || !tags.length) return 'нет тегов';

    return tags.join(', ');
};

const getSizeRulesText = (rules) => {
    if (!Array.isArray(rules) || !rules.length) return 'нет размеров';

    return rules
        .map((rule) => {
            const size = formatMillingDimensions(rule);
            const price = fmtMoney(rule.price);

            return `${size} — ${price}`;
        })
        .join('; ');
};

const buildChangeSummary = (currentForm, cleanForm, product, context = {}) => {
    const current = buildPayload(currentForm, product, context);
    const clean = buildPayload(cleanForm, product, context);

    const currentPricing = current.millingPricing || {};
    const cleanPricing = clean.millingPricing || {};
    const currentProductPricing = current.productPricing || {};
    const cleanProductPricing = clean.productPricing || {};
    const changes = [];

    if (current.materialSelectionMode !== clean.materialSelectionMode) {
        changes.push({
            label: 'Тип выбора материала',
            before: getMaterialSelectionModeText(clean.materialSelectionMode),
            after: getMaterialSelectionModeText(current.materialSelectionMode),
        });
    }

    if (!!currentProductPricing.enabled !== !!cleanProductPricing.enabled) {
        changes.push({
            label: 'Формула изделия',
            before: getBoolText(cleanProductPricing.enabled),
            after: getBoolText(currentProductPricing.enabled),
        });
    }

    if (currentProductPricing.mode !== cleanProductPricing.mode) {
        changes.push({
            label: 'Способ расчёта изделия',
            before: getFormulaText(cleanProductPricing.mode),
            after: getFormulaText(currentProductPricing.mode),
        });
    }

    if (currentProductPricing.blankMaterialKey !== cleanProductPricing.blankMaterialKey) {
        changes.push({
            label: 'Материал заготовки',
            before: cleanProductPricing.blankMaterialName || cleanProductPricing.blankMaterialKey || 'не выбран',
            after: currentProductPricing.blankMaterialName || currentProductPricing.blankMaterialKey || 'не выбран',
        });
    }

    if (Number(currentProductPricing.blankPriceOverride || 0) !== Number(cleanProductPricing.blankPriceOverride || 0)) {
        changes.push({
            label: 'Цена заготовки вручную',
            before: fmtMoney(cleanProductPricing.blankPriceOverride),
            after: fmtMoney(currentProductPricing.blankPriceOverride),
        });
    }

    if (currentProductPricing.blankPolishingType !== cleanProductPricing.blankPolishingType) {
        changes.push({
            label: 'Полировка заготовки',
            before: cleanProductPricing.blankPolishingType || 'two-side',
            after: currentProductPricing.blankPolishingType || 'two-side',
        });
    }

    if ((currentProductPricing.allowEstimatedBlankPrice !== false) !== (cleanProductPricing.allowEstimatedBlankPrice !== false)) {
        changes.push({
            label: 'Предполагаемая цена заготовки',
            before: cleanProductPricing.allowEstimatedBlankPrice === false ? 'запрещена' : 'разрешена',
            after: currentProductPricing.allowEstimatedBlankPrice === false ? 'запрещена' : 'разрешена',
        });
    }

    if (Number(currentProductPricing.totalMarkupPercent || 0) !== Number(cleanProductPricing.totalMarkupPercent || 0)) {
        changes.push({
            label: 'Общая наценка изделия',
            before: `${cleanProductPricing.totalMarkupPercent || 0}%`,
            after: `${currentProductPricing.totalMarkupPercent || 0}%`,
        });
    }

    if (Number(currentProductPricing.minimumPrice || 0) !== Number(cleanProductPricing.minimumPrice || 0)) {
        changes.push({
            label: 'Минимальная цена изделия',
            before: fmtMoney(cleanProductPricing.minimumPrice),
            after: fmtMoney(currentProductPricing.minimumPrice),
        });
    }

    if (Number(currentProductPricing.manualPrice || 0) !== Number(cleanProductPricing.manualPrice || 0)) {
        changes.push({
            label: 'Ручная цена изделия',
            before: fmtMoney(cleanProductPricing.manualPrice),
            after: fmtMoney(currentProductPricing.manualPrice),
        });
    }

    if (Number(currentProductPricing.basePrice || 0) !== Number(cleanProductPricing.basePrice || 0)) {
        changes.push({
            label: 'Базовая цена изделия',
            before: fmtMoney(cleanProductPricing.basePrice),
            after: fmtMoney(currentProductPricing.basePrice),
        });
    }

    if (!isSameMillingDimensions(currentProductPricing.baseDimensions, cleanProductPricing.baseDimensions)) {
        changes.push({
            label: 'Базовый размер изделия',
            before: formatMillingDimensions(cleanProductPricing.baseDimensions),
            after: formatMillingDimensions(currentProductPricing.baseDimensions),
        });
    }

    if (Number(currentProductPricing.setupPercent || 0) !== Number(cleanProductPricing.setupPercent || 0)) {
        changes.push({
            label: 'Наценка изделия',
            before: `${cleanProductPricing.setupPercent || 0}%`,
            after: `${currentProductPricing.setupPercent || 0}%`,
        });
    }

    if (Number(currentProductPricing.roundTo || 0) !== Number(cleanProductPricing.roundTo || 0)) {
        changes.push({
            label: 'Округление изделия',
            before: `${cleanProductPricing.roundTo || 0} ₽`,
            after: `${currentProductPricing.roundTo || 0} ₽`,
        });
    }

    if (Number(currentProductPricing.materialDensityKgM3 || 0) !== Number(cleanProductPricing.materialDensityKgM3 || 0)) {
        changes.push({
            label: 'Плотность камня',
            before: `${cleanProductPricing.materialDensityKgM3 || 0} кг/м³`,
            after: `${currentProductPricing.materialDensityKgM3 || 0} кг/м³`,
        });
    }

    if (JSON.stringify(currentProductPricing.sizeRules || []) !== JSON.stringify(cleanProductPricing.sizeRules || [])) {
        changes.push({
            label: 'Размеры изделия на сайте',
            before: getSizeRulesText(cleanProductPricing.sizeRules),
            after: getSizeRulesText(currentProductPricing.sizeRules),
        });
    }

    if (current.workType !== clean.workType || current.workLabel !== clean.workLabel) {
        changes.push({
            label: 'Работа',
            before: clean.workType === 'none' ? 'не показывается' : (clean.workLabel || clean.workType || '—'),
            after: current.workType === 'none' ? 'не показывается' : (current.workLabel || current.workType || '—'),
        });
    }

    if (current.workCalcMode !== clean.workCalcMode) {
        changes.push({
            label: 'Старый тип расчёта',
            before: getFormulaText(clean.workCalcMode),
            after: getFormulaText(current.workCalcMode),
        });
    }

    if (Number(current.workPrice || 0) !== Number(clean.workPrice || 0)) {
        changes.push({
            label: 'Старая цена фрезеровки',
            before: fmtMoney(clean.workPrice),
            after: fmtMoney(current.workPrice),
        });
    }

    if (!!currentPricing.enabled !== !!cleanPricing.enabled) {
        changes.push({
            label: 'Формула фрезеровки',
            before: getBoolText(cleanPricing.enabled),
            after: getBoolText(currentPricing.enabled),
        });
    }

    if (currentPricing.mode !== cleanPricing.mode) {
        changes.push({
            label: 'Формула',
            before: getFormulaText(cleanPricing.mode),
            after: getFormulaText(currentPricing.mode),
        });
    }

    if (currentPricing.axisPreset !== cleanPricing.axisPreset) {
        changes.push({
            label: 'Оси GLB',
            before: getAxisText(cleanPricing.axisPreset),
            after: getAxisText(currentPricing.axisPreset),
        });
    }

    if (Number(currentPricing.basePrice || 0) !== Number(cleanPricing.basePrice || 0)) {
        changes.push({
            label: 'Цена клиенту базовой фрезеровки',
            before: fmtMoney(cleanPricing.basePrice),
            after: fmtMoney(currentPricing.basePrice),
        });
    }

    if (!isSameMillingDimensions(currentPricing.baseDimensions, cleanPricing.baseDimensions)) {
        changes.push({
            label: 'Базовый размер фрезеровки',
            before: formatMillingDimensions(cleanPricing.baseDimensions),
            after: formatMillingDimensions(currentPricing.baseDimensions),
        });
    }

    if (Number(currentPricing.setupPercent || 0) !== Number(cleanPricing.setupPercent || 0)) {
        changes.push({
            label: 'Наценка фрезеровки',
            before: `${cleanPricing.setupPercent || 0}%`,
            after: `${currentPricing.setupPercent || 0}%`,
        });
    }

    if (Number(currentPricing.roundTo || 0) !== Number(cleanPricing.roundTo || 0)) {
        changes.push({
            label: 'Округление фрезеровки',
            before: `${cleanPricing.roundTo || 0} ₽`,
            after: `${currentPricing.roundTo || 0} ₽`,
        });
    }

    if (JSON.stringify(currentPricing.sizeRules || []) !== JSON.stringify(cleanPricing.sizeRules || [])) {
        changes.push({
            label: 'Размеры фрезеровки на сайте',
            before: getSizeRulesText(cleanPricing.sizeRules),
            after: getSizeRulesText(currentPricing.sizeRules),
        });
    }

    if (current.modelPartsEnabled !== clean.modelPartsEnabled) {
        changes.push({
            label: 'Разбор 3D-модели',
            before: getBoolText(clean.modelPartsEnabled),
            after: getBoolText(current.modelPartsEnabled),
        });
    }

    if (JSON.stringify(current.hashtags || []) !== JSON.stringify(clean.hashtags || [])) {
        changes.push({
            label: 'Хештеги',
            before: getTagsText(clean.hashtags),
            after: getTagsText(current.hashtags),
        });
    }

    return changes;
};

const getConfirmTitle = (action) => {
    if (action?.type === 'close') {
        return 'Закрыть без сохранения?';
    }

    if (action?.type === 'select' || action?.type === 'category') {
        return 'Перейти к другому товару?';
    }

    return 'Есть несохранённые изменения';
};

const getConfirmText = (action) => {
    if (action?.type === 'close') {
        return 'Ты изменил настройки текущего товара. Перед закрытием выбери, сохранить изменения или закрыть без сохранения.';
    }

    if (action?.type === 'select' || action?.type === 'category') {
        return 'Ты изменил настройки текущего товара. Перед переходом к другому товару выбери, сохранить изменения или перейти без сохранения.';
    }

    return 'Перед продолжением нужно выбрать действие.';
};

const getDiscardButtonText = (action) => {
    if (action?.type === 'close') {
        return 'Закрыть без сохранения';
    }

    if (action?.type === 'select' || action?.type === 'category') {
        return 'Перейти без сохранения';
    }

    return 'Продолжить без сохранения';
};

const getSaveButtonText = (action, saving) => {
    if (saving) return 'Сохраняю...';

    if (action?.type === 'close') {
        return 'Сохранить и закрыть';
    }

    if (action?.type === 'select' || action?.type === 'category') {
        return 'Сохранить и перейти';
    }

    return 'Сохранить и продолжить';
};

const clonePricingDimensions = (value) => (
    value && typeof value === 'object'
        ? { ...value }
        : value
);

const keepCurrentSharedDimensions = (
    nextPricing = {},
    currentPricing = {},
    {
        includeAxisPreset = false,
    } = {}
) => ({
    ...(nextPricing || {}),
    ...(currentPricing?.baseDimensions ? {
        baseDimensions: clonePricingDimensions(
            currentPricing.baseDimensions
        ),
    } : {}),
    ...(currentPricing?.testDimensions ? {
        testDimensions: clonePricingDimensions(
            currentPricing.testDimensions
        ),
    } : {}),
    ...(includeAxisPreset && currentPricing?.axisPreset ? {
        axisPreset: currentPricing.axisPreset,
    } : {}),
});

const cloneMillingPricing = (pricing = {}) => ({
    ...(pricing || {}),
    baseDimensions: clonePricingDimensions(pricing?.baseDimensions),
    testDimensions: clonePricingDimensions(pricing?.testDimensions),
    sizeRules: Array.isArray(pricing?.sizeRules)
        ? pricing.sizeRules.map((rule) => ({ ...rule }))
        : [],
});

const getCanonicalMillingMaterialKey = (sourceForm = {}) => {
    const variants = Array.isArray(sourceForm?.materialPricingVariants)
        ? sourceForm.materialPricingVariants
        : [];

    if (variants.some((variant) => variant?.materialKey === 'gabbro-diabase')) {
        return 'gabbro-diabase';
    }

    return String(
        sourceForm?.defaultMaterialKey
        || variants.find((variant) => variant?.isDefault === true)?.materialKey
        || variants[0]?.materialKey
        || 'gabbro-diabase'
    );
};

const resolveCanonicalMillingPricing = (sourceForm = {}, explicitPricing = null) => {
    if (explicitPricing && typeof explicitPricing === 'object') {
        return cloneMillingPricing(explicitPricing);
    }

    const variants = Array.isArray(sourceForm?.materialPricingVariants)
        ? sourceForm.materialPricingVariants
        : [];
    const canonicalKey = getCanonicalMillingMaterialKey(sourceForm);
    const activeKey = String(sourceForm?.activeMaterialKey || '');

    if (activeKey === canonicalKey && sourceForm?.millingPricing) {
        return cloneMillingPricing(sourceForm.millingPricing);
    }

    const canonicalVariant = variants.find((variant) => (
        String(variant?.materialKey || '') === canonicalKey
    ));

    return cloneMillingPricing(
        canonicalVariant?.millingPricing
        || sourceForm?.millingPricing
        || {}
    );
};

const createIndependentMaterialVariant = ({
    materialKey,
    catalog,
    sourceForm,
}) => {
    const currentProductPricing = sourceForm?.productPricing || {};
    const currentMillingPricing = sourceForm?.millingPricing || {};

    return {
        materialKey,
        materialName: catalog?.name || '',
        enabled: true,
        isDefault: false,
        productPricing: {
            enabled: currentProductPricing.enabled === true,
            mode: currentProductPricing.mode || 'blankPlusMilling',
            basePrice: '',
            baseDimensions: clonePricingDimensions(
                currentProductPricing.baseDimensions
            ),
            testDimensions: clonePricingDimensions(
                currentProductPricing.testDimensions
            ),
            setupPercent: currentProductPricing.setupPercent ?? 10,
            roundTo: Number(
                catalog?.defaultRoundTo
                || currentProductPricing.roundTo
                || 100
            ),
            materialDensityKgM3: Number(
                catalog?.densityKgM3 || 3070
            ),
            materialDensityLabel: catalog?.name || '',
            blankMaterialKey: materialKey,
            blankMaterialName: catalog?.name || '',
            blankPriceOverride: 0,
            blankType: currentProductPricing.blankType || 'stele',
            blankPolishingType: currentProductPricing.blankPolishingType || 'two-side',
            blankSupplierKey: '',
            allowEstimatedBlankPrice: true,
            totalMarkupPercent: Number(
                catalog?.defaultMarkupPercent
                ?? currentProductPricing.totalMarkupPercent
                ?? 20
            ),
            minimumPrice: Number(
                catalog?.defaultMinimumPrice ?? 0
            ),
            manualPrice: 0,
            blankCatalogRevision: 0,
            blankPriceCalculated: 0,
            millingPriceCalculated: 0,
            subtotalCalculated: 0,
            calculatedPrice: 0,
            calculationSource: '',
            calculatedAt: null,
            sizeRules: Array.isArray(currentProductPricing.sizeRules)
                ? currentProductPricing.sizeRules.map((rule) => ({ ...rule }))
                : [],
        },
        millingPricing: {
            ...currentMillingPricing,
            enabled: currentMillingPricing.enabled === true,
            mode: currentMillingPricing.mode || 'volumeForward',
            basePrice: currentMillingPricing.basePrice ?? '',
            baseDimensions: clonePricingDimensions(
                currentMillingPricing.baseDimensions
            ),
            testDimensions: clonePricingDimensions(
                currentMillingPricing.testDimensions
            ),
            axisPreset: currentMillingPricing.axisPreset || 'auto',
            setupPercent: currentMillingPricing.setupPercent ?? 10,
            complexityMultiplier: currentMillingPricing.complexityMultiplier ?? 1,
            roundTo: Number(currentMillingPricing.roundTo || 100),
            manualEnabled: currentMillingPricing.manualEnabled === true,
            manualPrice: currentMillingPricing.manualPrice ?? 0,
            sizeRules: Array.isArray(currentMillingPricing.sizeRules)
                ? currentMillingPricing.sizeRules.map((rule) => ({ ...rule }))
                : [],
        },
        textureBinding: {
            mode: 'auto',
            applyToAll: true,
            materialIndexes: [],
        },
    };
};

const EMPTY_BLANK_PRICE_CATALOGS = Object.freeze([]);

const AdminProduct3DPreview = ({
    isOpen,
    product,
    products = [],
    categories = [],
    mode = 'editor',
    modelDimensions = null,
    blankDimensions = null,
    catalogDimensions: _catalogDimensions = null,
    showBlankFrame: initialShowBlankFrame = true,
    viewerTitle = '',
    embedded = false,
    onBack = null,
    backLabel = 'Цена и размеры',
    onClose,
    onSelectProduct,
    onProductUpdated,
    realtimeCamera = null,
    realtimeOperations = {},
    onRealtimeOperation = null,
    sharedViewState = null,
    onSharedViewStateChange = null,
    blankPriceCatalogs: blankPriceCatalogsProp = EMPTY_BLANK_PRICE_CATALOGS,
}) => {
    const dispatch = useDispatch();
    const isViewerMode = mode === 'viewer';
    const mountedRef = useRef(true);
    const [loadedBlankPriceCatalogs, setLoadedBlankPriceCatalogs] = useState([]);
    const [pricingPolicy, setPricingPolicy] = useState(null);
    const safeBlankPriceCatalogsProp = Array.isArray(blankPriceCatalogsProp)
        && blankPriceCatalogsProp.length
        ? blankPriceCatalogsProp
        : EMPTY_BLANK_PRICE_CATALOGS;
    // Локальная загрузка имеет приоритет над prop: родитель страницы может
    // держать старую копию справочника после изменения коэффициентов материала.
    // Пустой fallback обязан иметь стабильную ссылку, иначе effect инициализации
    // формы получает новый [] на каждом render и способен уйти в update-depth loop.
    const blankPriceCatalogs = loadedBlankPriceCatalogs.length
        ? loadedBlankPriceCatalogs
        : safeBlankPriceCatalogsProp;

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const refreshBlankPriceCatalogs = useCallback(async () => {
        try {
            const response = await getBlankPriceCatalogs();
            const materials = Array.isArray(response?.materials) ? response.materials : [];
            if (mountedRef.current && materials.length) {
                setLoadedBlankPriceCatalogs(materials);
            }
            return materials;
        } catch (error) {
            // Если запрос временно не прошёл, оставляем последний локальный
            // справочник или prop родителя, чтобы редактор не обнулял цены.
            return [];
        }
    }, []);

    useEffect(() => {
        if (!isOpen || embedded || typeof document === 'undefined') {
            return undefined;
        }

        const body = document.body;
        const root = document.documentElement;
        const previousBodyOverflow = body.style.overflow;
        const previousBodyOverscroll = body.style.overscrollBehavior;
        const previousRootOverflow = root.style.overflow;
        const previousRootOverscroll = root.style.overscrollBehavior;

        body.style.overflow = 'hidden';
        body.style.overscrollBehavior = 'none';
        root.style.overflow = 'hidden';
        root.style.overscrollBehavior = 'none';

        return () => {
            body.style.overflow = previousBodyOverflow;
            body.style.overscrollBehavior = previousBodyOverscroll;
            root.style.overflow = previousRootOverflow;
            root.style.overscrollBehavior = previousRootOverscroll;
        };
    }, [embedded, isOpen]);

    useEffect(() => {
        if (!isOpen) return undefined;
        refreshBlankPriceCatalogs();
        return undefined;
    }, [isOpen, refreshBlankPriceCatalogs]);

    useEffect(() => {
        if (!isOpen) return undefined;

        const socket = getSocket();
        const handleBlankPricesUpdated = () => {
            refreshBlankPriceCatalogs();
        };

        socket.on('catalogReview:blankPricesUpdated', handleBlankPricesUpdated);

        return () => {
            socket.off('catalogReview:blankPricesUpdated', handleBlankPricesUpdated);
        };
    }, [isOpen, refreshBlankPriceCatalogs]);

    useEffect(() => {
        if (!isOpen || isViewerMode) return undefined;
        let alive = true;

        getPricingPolicy()
            .then((response) => {
                if (alive) setPricingPolicy(response?.policy || null);
            })
            .catch(() => {
                if (alive) setPricingPolicy(null);
            });

        return () => {
            alive = false;
        };
    }, [isOpen, isViewerMode]);

    // catalogDimensions оставлен только для совместимости со старыми вызовами.
    void _catalogDimensions;

    const officeState = useSelector((state) => state.office || {});
    const modelsData = officeState.modelsData || {};
    const progressMap = officeState.modelLoadingProgress || {};

    const [viewMode, setViewMode] = useState(() => (
        loadAdminProduct3DSceneSettings().backgroundTheme || 'light'
    ));
    const [loadError, setLoadError] = useState('');
    const [navCategoryId, setNavCategoryId] = useState('');
    const [savedProduct, setSavedProduct] = useState(null);
    const [form, setForm] = useState(getInitialForm(product, { blankPriceCatalogs }));
    const [cleanForm, setCleanForm] = useState(getInitialForm(product, { blankPriceCatalogs }));
    const [saving, setSaving] = useState(false);
    const [saveStatus, setSaveStatus] = useState(null);
    const [pendingAction, setPendingAction] = useState(null);
    const [showDimensions, setShowDimensions] = useState(true);
    const [showBlankDimensions, setShowBlankDimensions] = useState(false);
    const [showBlankFrame, setShowBlankFrame] = useState(
        initialShowBlankFrame !== false
    );
    const [stageFullscreen, setStageFullscreen] = useState(false);
    const [displayControlsOpen, setDisplayControlsOpen] = useState(false);
    const [sceneToolPanels, setSceneToolPanels] = useState({
        background: false,
        lighting: false,
    });
    const [editorTab, setEditorTab] = useState('prices');
    const [sceneSettings, setSceneSettings] = useState(
        loadAdminProduct3DSceneSettings
    );
    const [priceModeTab, setPriceModeTab] = useState('product');
    const viewportCameraStateRef = useRef(null);

    useEffect(() => {
        saveAdminProduct3DSceneSettings(sceneSettings);
    }, [sceneSettings]);

    const productId = product?._id ? String(product._id) : '';

    useEffect(() => {
        if (isOpen && productId) {
            setPriceModeTab('product');
        }
    }, [isOpen, productId]);
    const catalogNumber = Number(
        product?._catalogReviewPurchasedNumber || 0
    );
    const semantic3DNamespace = `admin-3d:${catalogNumber || productId || 'unknown'}`;
    const hasDomainSharedView = Boolean(sharedViewState || onSharedViewStateChange);

    const publish3DViewField = useCallback((key, value) => {
        if (typeof onSharedViewStateChange === 'function') {
            onSharedViewStateChange(key, value);
            return;
        }

        publishAdminRealtimeSemanticState({
            namespace: semantic3DNamespace,
            stateKey: key,
            value,
        });
    }, [onSharedViewStateChange, semantic3DNamespace]);
    const modelOperationKey = `model:load:${catalogNumber || productId || 'unknown'}`;
    const remoteModelOperation = realtimeOperations?.[modelOperationKey] || null;

    const localProduct = useMemo(() => {
        if (savedProduct && String(savedProduct._id) === String(productId)) {
            return savedProduct;
        }

        return product;
    }, [savedProduct, product, productId]);

    const modelData = productId ? modelsData[productId] : null;
    const progress = productId ? progressMap[productId] : null;
    const hasModel = Boolean(modelData?.geometry);
    const isLoading = Boolean(progress) && !hasModel;

    const realDimensions = useMemo(() => {
        return getPreviewDimensions(localProduct, modelData);
    }, [localProduct, modelData]);

    const viewerAxisPreset = (
        localProduct?._catalogReviewOrientation
        || localProduct?.millingPricing?.axisPreset
        || 'vertical'
    );

    const requestedModelDimensions = (
        modelDimensions
        || localProduct?._catalogReviewModelDimensions
        || null
    );

    const requestedBlankDimensions = (
        blankDimensions
        || requestedModelDimensions
        || null
    );

    const modelSceneDimensions = useMemo(() => {
        if (!isViewerMode) return null;

        /*
         * В режиме каталога размер, выбранный в модалке проверки,
         * является целевым размером объекта. Поэтому геометрия и её
         * размерные линии масштабируются синхронно.
         */
        const selectedDimensions = viewerDimensionsToScene(
            requestedModelDimensions,
            viewerAxisPreset,
            'выбранный размер формы'
        );

        if (selectedDimensions) return selectedDimensions;

        /*
         * До загрузки/получения выбранного размера используем стандартные
         * размеры фактически загруженной геометрии как безопасный fallback.
         */
        if (realDimensions) {
            return {
                ...realDimensions,
                scaleBase: {
                    x: Number(realDimensions.x || 0),
                    y: Number(realDimensions.y || 0),
                    z: Number(realDimensions.z || 0),
                },
                axisPreset: viewerAxisPreset,
                source: 'исходная геометрия 3D',
            };
        }

        return null;
    }, [
        isViewerMode,
        realDimensions,
        requestedModelDimensions,
        viewerAxisPreset,
    ]);

    const editorProductDimensions = (
        form?.millingPricing?.testDimensions
        || form?.productPricing?.testDimensions
        || form?.productPricing?.baseDimensions
        || form?.millingPricing?.baseDimensions
        || null
    );
    const editorBlankDimensions = useMemo(() => (
        editorProductDimensions
            ? resolveBlankDimensions(
                editorProductDimensions,
                form?.productPricing || {},
                pricingPolicy || {}
            )
            : null
    ), [editorProductDimensions, form?.productPricing, pricingPolicy]);

    const blankSceneDimensions = useMemo(() => {
        const sourceDimensions = isViewerMode
            ? requestedBlankDimensions
            : editorBlankDimensions;
        const axisPreset = isViewerMode
            ? viewerAxisPreset
            : (form?.millingPricing?.axisPreset || viewerAxisPreset);

        return viewerDimensionsToScene(
            sourceDimensions,
            axisPreset,
            isViewerMode ? 'размер заготовки' : 'текущий размер заготовки'
        );
    }, [
        editorBlankDimensions,
        form?.millingPricing?.axisPreset,
        isViewerMode,
        requestedBlankDimensions,
        viewerAxisPreset,
    ]);

    const sceneDimensions = useMemo(() => {
        if (isViewerMode) {
            return modelSceneDimensions || realDimensions;
        }

        return getSceneDimensionsFromMillingForm(form, localProduct, realDimensions);
    }, [
        form,
        isViewerMode,
        localProduct,
        modelSceneDimensions,
        realDimensions,
    ]);

    const activeMaterialCatalog = useMemo(() => {
        const key = form?.activeMaterialKey || form?.defaultMaterialKey || form?.productPricing?.blankMaterialKey || 'gabbro-diabase';
        return blankPriceCatalogs.find((item) => item.key === key) || null;
    }, [blankPriceCatalogs, form?.activeMaterialKey, form?.defaultMaterialKey, form?.productPricing?.blankMaterialKey]);

    const graniteCommonDimensions = useMemo(() => (
        requestedModelDimensions
        || form?.productPricing?.testDimensions
        || form?.productPricing?.baseDimensions
        || form?.millingPricing?.testDimensions
        || form?.millingPricing?.baseDimensions
        || null
    ), [
        requestedModelDimensions,
        form?.productPricing?.testDimensions,
        form?.productPricing?.baseDimensions,
        form?.millingPricing?.testDimensions,
        form?.millingPricing?.baseDimensions,
    ]);

    const graniteWorkOptions = useMemo(() => {
        const currentKey = String(form?.workKey || form?.workType || 'milling');
        const source = Array.isArray(form?.workOptions) ? form.workOptions : [];
        const current = normalizeWorkOption({
            key: currentKey,
            name: form?.workName,
            enabled: form?.workEnabled,
            workType: form?.workType,
            calcMode: form?.workCalcMode,
            price: form?.workPrice,
            pricePerMeter: form?.workPricePerMeter,
            pricePerSquareMeter: form?.workPricePerSquareMeter,
            pricePerCubicMeter: form?.workPricePerCubicMeter,
            pricing: form?.millingPricing,
        }, localProduct);
        let found = false;
        const next = source.map((item, index) => {
            const normalized = normalizeWorkOption(item, localProduct, index);
            if (String(normalized.key) !== currentKey) return normalized;
            found = true;
            return current;
        });
        if (!found) next.push(current);
        return next;
    }, [
        form?.workOptions, form?.workKey, form?.workName, form?.workEnabled, form?.workType,
        form?.workCalcMode, form?.workPrice, form?.workPricePerMeter, form?.workPricePerSquareMeter,
        form?.workPricePerCubicMeter, form?.millingPricing, localProduct,
    ]);

    const granitePriceSummaryByKey = useMemo(() => {
        const variants = Array.isArray(form?.materialPricingVariants)
            ? form.materialPricingVariants
            : [];

        return variants.reduce((result, variant) => {
            const materialKey = String(variant?.materialKey || '').trim();
            if (!materialKey) return result;

            const variantMillingPricing = normalizeMillingPricing(
                variant?.millingPricing,
                localProduct
            );
            const variantProductPricing = normalizeProductPricing(
                variant?.productPricing,
                localProduct,
                blankPriceCatalogs
            );
            const isDefaultVariant = (
                variant?.isDefault === true
                || materialKey === form?.defaultMaterialKey
            );
            const legacyMillingFallback = isDefaultVariant
                ? form?.workPrice
                : 0;
            const legacyProductFallback = isDefaultVariant
                ? localProduct?.price
                : 0;
            const variantMaterialCatalog = blankPriceCatalogs.find((item) => (
                String(item?.key || '') === materialKey
            )) || null;
            const millingPreview = applyMaterialMillingMarkup(
                calcMillingPrice(
                    variantMillingPricing,
                    graniteCommonDimensions,
                    legacyMillingFallback,
                    pricingPolicy || {}
                ),
                variantMaterialCatalog,
                pricingPolicy || {}
            );
            const productPreview = calcProductPrice(
                variantProductPricing,
                graniteCommonDimensions,
                legacyProductFallback,
                {
                    blankPriceCatalogs,
                    millingPricing: variantMillingPricing,
                    millingFallbackPrice: legacyMillingFallback,
                    pricingPolicy,
                    workOptions: graniteWorkOptions,
                }
            );
            const customerPrices = calcProductCustomerPrices({
                blankPrice: productPreview?.blankPrice,
                millingPrice: productPreview?.millingPrice || millingPreview?.price,
                worksPrice: productPreview?.worksPrice,
                fallbackPrice: productPreview?.price,
                pricingPolicy: pricingPolicy || {},
            });

            result[materialKey] = {
                // Rail в канвасе использует те же конечные цены, что и
                // финансовый блок справа: подписка и обычная продажа.
                productPrice: customerPrices.publicPrice || productPreview?.price || 0,
                subscriberPrice: customerPrices.subscriberPrice || 0,
                publicPrice: customerPrices.publicPrice || 0,
                millingPrice: millingPreview?.price || 0,
                productEstimated: productPreview?.blankEstimated === true,
                productRequestRequired: productPreview?.blankRequestRequired === true,
            };

            return result;
        }, {});
    }, [
        blankPriceCatalogs,
        form?.materialPricingVariants,
        form?.workPrice,
        graniteCommonDimensions,
        localProduct,
        pricingPolicy,
        graniteWorkOptions,
    ]);
    const activeGranitePriceSummary = granitePriceSummaryByKey[
        form?.activeMaterialKey || form?.defaultMaterialKey || 'gabbro-diabase'
    ] || {};

    const unpolishedSurfaceCatalog = useMemo(
        () => getUnpolishedSurfaceCatalog(blankPriceCatalogs),
        [blankPriceCatalogs]
    );

    const activeMaterialVariant = useMemo(() => (
        (Array.isArray(form?.materialPricingVariants) ? form.materialPricingVariants : [])
            .find((variant) => variant.materialKey === (form?.activeMaterialKey || form?.defaultMaterialKey)) || null
    ), [form?.materialPricingVariants, form?.activeMaterialKey, form?.defaultMaterialKey]);

    const usesGraniteMaterialSelection = form?.materialSelectionMode === 'granite';

    const isDirty = useMemo(() => {
        if (isViewerMode) return false;
        return !isSamePayload(form, cleanForm, localProduct, { blankPriceCatalogs, pricingPolicy });
    }, [form, cleanForm, localProduct, isViewerMode, blankPriceCatalogs, pricingPolicy]);

    const changeSummary = useMemo(() => {
        if (!isDirty) return [];

        return buildChangeSummary(form, cleanForm, localProduct, { blankPriceCatalogs });
    }, [form, cleanForm, localProduct, isDirty, blankPriceCatalogs]);

    const productCategoryId = useMemo(() => {
        return product ? getProductCategoryValue(product) : '';
    }, [product]);

    useEffect(() => {
        if (!productId) return;

        const nextForm = getInitialForm(product, { blankPriceCatalogs });

        // Камера относится к конкретной модели. При переходе между товарами
        // не переносим старый ракурс, иначе следующая модель может открыться
        // боком даже при корректной ориентации геометрии.
        viewportCameraStateRef.current = null;
        setSavedProduct(null);
        setForm(nextForm);
        setCleanForm(nextForm);
        setSaveStatus(null);
        setPendingAction(null);
        setStageFullscreen(false);
        setDisplayControlsOpen(false);
        setSceneToolPanels({ background: false, lighting: false });
    }, [productId, product]);

    useEffect(() => {
        if (!sharedViewState || !isViewerMode) return;

        if (['light', 'studio', 'dark'].includes(sharedViewState.viewerMode)) {
            setViewMode(sharedViewState.viewerMode);
        }

        if (typeof sharedViewState.showDimensions === 'boolean') {
            setShowDimensions(sharedViewState.showDimensions);
        }

        if (typeof sharedViewState.showBlankDimensions === 'boolean') {
            setShowBlankDimensions(sharedViewState.showBlankDimensions);
        }

        if (typeof sharedViewState.showBlankFrame === 'boolean') {
            setShowBlankFrame(sharedViewState.showBlankFrame);
        }

        if (typeof sharedViewState.stageFullscreen === 'boolean') {
            setStageFullscreen(sharedViewState.stageFullscreen);
        }

        if (typeof sharedViewState.displayControlsOpen === 'boolean') {
            setDisplayControlsOpen(sharedViewState.displayControlsOpen);
        }

        if (typeof sharedViewState.sceneBackgroundPanelOpen === 'boolean'
            || typeof sharedViewState.sceneLightingPanelOpen === 'boolean') {
            setSceneToolPanels((previous) => ({
                background: typeof sharedViewState.sceneBackgroundPanelOpen === 'boolean'
                    ? sharedViewState.sceneBackgroundPanelOpen
                    : previous.background,
                lighting: typeof sharedViewState.sceneLightingPanelOpen === 'boolean'
                    ? sharedViewState.sceneLightingPanelOpen
                    : previous.lighting,
            }));
        }

        if (typeof sharedViewState.activeMaterialKey === 'string' && sharedViewState.activeMaterialKey) {
            setForm((previous) => applyActiveMaterialSelectionToForm(
                previous,
                sharedViewState.activeMaterialKey
            ));
        }

        setSceneSettings((previous) => {
            const patch = {};
            const numericKeys = [
                'sunAngle',
                'sunVertical',
                'sunIntensity',
                'fillIntensity',
                'hemisphereIntensity',
                'ambientIntensity',
                'rimIntensity',
                'dimensionLabelScale',
            ];
            const booleanKeys = [
                'shadowsEnabled',
                'backgroundAsEnvironment',
                'showGround',
            ];

            numericKeys.forEach((key) => {
                if (Number.isFinite(Number(sharedViewState[key]))) {
                    patch[key] = Number(sharedViewState[key]);
                }
            });

            booleanKeys.forEach((key) => {
                if (typeof sharedViewState[key] === 'boolean') {
                    patch[key] = sharedViewState[key];
                }
            });

            if (
                ['low', 'medium', 'high'].includes(
                    sharedViewState.graphicsQuality
                )
            ) {
                patch.graphicsQuality = sharedViewState.graphicsQuality;
            }

            if (
                ['theme', 'panorama'].includes(
                    sharedViewState.backgroundMode
                )
            ) {
                patch.backgroundMode = sharedViewState.backgroundMode;
            }

            if (
                ['light', 'studio', 'dark'].includes(
                    sharedViewState.backgroundTheme
                )
            ) {
                patch.backgroundTheme = sharedViewState.backgroundTheme;
            } else if (
                ['light', 'studio', 'dark'].includes(
                    sharedViewState.viewerMode
                )
            ) {
                patch.backgroundTheme = sharedViewState.viewerMode;
            }

            if (typeof sharedViewState.backgroundUrl === 'string') {
                patch.backgroundUrl = sharedViewState.backgroundUrl;
            }

            return normalizeAdminProduct3DSceneSettings({
                ...previous,
                ...patch,
            });
        });
    }, [
        isViewerMode,
        sharedViewState?.viewerMode,
        sharedViewState?.showDimensions,
        sharedViewState?.showBlankDimensions,
        sharedViewState?.showBlankFrame,
        sharedViewState?.stageFullscreen,
        sharedViewState?.displayControlsOpen,
        sharedViewState?.sceneBackgroundPanelOpen,
        sharedViewState?.sceneLightingPanelOpen,
        sharedViewState?.activeMaterialKey,
        sharedViewState?.sunAngle,
        sharedViewState?.sunVertical,
        sharedViewState?.sunIntensity,
        sharedViewState?.shadowsEnabled,
        sharedViewState?.graphicsQuality,
        sharedViewState?.dimensionLabelScale,
        sharedViewState?.fillIntensity,
        sharedViewState?.hemisphereIntensity,
        sharedViewState?.ambientIntensity,
        sharedViewState?.rimIntensity,
        sharedViewState?.backgroundMode,
        sharedViewState?.backgroundTheme,
        sharedViewState?.backgroundUrl,
        sharedViewState?.backgroundAsEnvironment,
        sharedViewState?.showGround,
    ]);

    useEffect(() => {
        if (!productId) return;
        if (isDirty) return;
        if (savedProduct && String(savedProduct._id) === String(productId)) return;

        const nextForm = getInitialForm(product, { blankPriceCatalogs });

        setForm(nextForm);
        setCleanForm(nextForm);
    }, [product, productId, isDirty, savedProduct, blankPriceCatalogs]);

    useEffect(() => {
        if (!isOpen || !productId || !realDimensions) return;

        setForm((prev) => {
            const currentPricing = prev?.millingPricing || {};
            const currentBase = currentPricing.baseDimensions || null;
            const axisPreset = currentPricing.axisPreset || 'auto';
            const dimensionsFromModel = getMillingDimensionsFromPreview(realDimensions, axisPreset);

            if (!dimensionsFromModel) return prev;

            const shouldHydrate =
                !currentBase ||
                isDefaultMillingDimensions(currentBase);

            if (!shouldHydrate) return prev;

            if (isSameMillingDimensions(currentBase, dimensionsFromModel)) return prev;

            const hydratedForm = {
                ...prev,
                millingPricing: {
                    ...currentPricing,
                    baseDimensions: {
                        ...dimensionsFromModel,
                        unit: 'mm',
                    },
                    testDimensions: {
                        ...dimensionsFromModel,
                        unit: 'mm',
                    },
                },
            };

            /*
              ВАЖНО:
              Это автоподстановка реального размера из GLB.
              Это НЕ ручное изменение пользователя.
              Поэтому одновременно обновляем и form, и cleanForm.
            */
            setCleanForm(hydratedForm);

            return hydratedForm;
        });
    }, [isOpen, productId, realDimensions]);

    useEffect(() => {
        if (!isOpen || !productCategoryId) return;

        setNavCategoryId((previous) => previous || productCategoryId);
    }, [isOpen, productCategoryId]);

    const categoriesForSelect = useMemo(() => {
        const map = new Map();

        if (Array.isArray(categories)) {
            categories.forEach((category) => {
                const value = getCategoryValue(category);
                const label = getCategoryTitle(category);

                if (value && !map.has(value)) {
                    map.set(value, { value, label });
                }
            });
        }

        if (Array.isArray(products)) {
            products.forEach((item) => {
                const value = getProductCategoryValue(item);
                const label = item?.categoryName || item?.categorySlug || 'Без категории';

                if (value && !map.has(value)) {
                    map.set(value, { value, label });
                }
            });
        }

        return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label, 'ru'));
    }, [categories, products]);

    const navigationProducts = useMemo(() => {
        const source = Array.isArray(products)
            ? products.filter((item) => item?._id && item?.modelPath)
            : [];

        if (!navCategoryId) {
            return source;
        }

        return source.filter((item) => {
            return String(getProductCategoryValue(item)) === String(navCategoryId);
        });
    }, [products, navCategoryId]);

    const activeIndex = useMemo(() => {
        if (!productId || !navigationProducts.length) {
            return -1;
        }

        return navigationProducts.findIndex((item) => {
            return String(item._id) === String(productId);
        });
    }, [navigationProducts, productId]);

    const canNavigate = navigationProducts.length > 1;

    const percent = useMemo(() => {
        if (!progress) return 0;

        const loaded = Number(progress.loaded) || 0;
        const total = Number(progress.total) || 0;

        if (!loaded || !total) return 0;

        return Math.min(100, Math.max(0, Math.round((loaded / total) * 100)));
    }, [progress]);

    const effectiveIsLoading = isLoading || Boolean(remoteModelOperation);
    const effectivePercent = isLoading
        ? percent
        : Number(remoteModelOperation?.progress || 0);

    useEffect(() => {
        if (!isOpen || !productId || !onRealtimeOperation) return;

        onRealtimeOperation({
            key: modelOperationKey,
            running: isLoading,
            progress: isLoading ? percent : (hasModel ? 100 : 0),
        });
    }, [
        hasModel,
        isLoading,
        isOpen,
        modelOperationKey,
        onRealtimeOperation,
        percent,
        productId,
    ]);

    useEffect(() => () => {
        onRealtimeOperation?.({
            key: modelOperationKey,
            running: false,
        });
    }, [modelOperationKey, onRealtimeOperation]);

    useEffect(() => {
        if (!isOpen || !productId) return undefined;
        if (modelData?.geometry) return undefined;

        let alive = true;

        setLoadError('');

        dispatch(getAdminProductModelData(product)).catch((error) => {
            if (!alive) return;

            setLoadError(error?.message || 'Не удалось загрузить 3D-модель товара');
        });

        return () => {
            alive = false;
        };
    }, [dispatch, isOpen, productId, modelData?.geometry, product]);

    const syncActiveVariant = useCallback((sourceForm, patch = {}) => {
        const activeKey = String(sourceForm?.activeMaterialKey || sourceForm?.defaultMaterialKey || 'gabbro-diabase');
        const variants = Array.isArray(sourceForm?.materialPricingVariants)
            ? sourceForm.materialPricingVariants
            : [];
        const canonicalMillingPricing = resolveCanonicalMillingPricing(
            sourceForm,
            Object.prototype.hasOwnProperty.call(patch, 'millingPricing')
                ? patch.millingPricing
                : null
        );

        return variants.map((variant) => ({
            ...variant,
            productPricing: variant.materialKey === activeKey
                ? (patch.productPricing ?? sourceForm.productPricing ?? variant.productPricing)
                : variant.productPricing,
            // Фрезеровка — одна базовая сетка для всех материалов.
            // Отличие породы задаётся только millingMarkupPercent каталога.
            millingPricing: cloneMillingPricing(canonicalMillingPricing),
        }));
    }, []);

    const applyFormFieldValue = useCallback((sourceForm, key, value) => {
        const previous = sourceForm && typeof sourceForm === 'object'
            ? sourceForm
            : {};
        const next = { ...previous, [key]: value };

        if (key === 'productPricing' || key === 'millingPricing') {
            next.materialPricingVariants = syncActiveVariant(previous, { [key]: value });
        }

        return next;
    }, [syncActiveVariant]);

    const publish3DFormField = useCallback((key, value) => {
        /*
         * CatalogReview имеет собственный revision/draft transport и не должен
         * получать второй параллельный канал формы. На ProductSeo/standalone
         * 3D нет domain sharedView, поэтому значения формы идут отдельным
         * semantic state. Это React-state sync, не DOM input replay.
         */
        if (hasDomainSharedView) return false;

        return publishAdminRealtimeSemanticState({
            namespace: semantic3DNamespace,
            stateKey: `form.${String(key || '')}`,
            value,
        });
    }, [hasDomainSharedView, semantic3DNamespace]);

    const handleChange = useCallback((key, value) => {
        setSaveStatus(null);
        setForm((previous) => applyFormFieldValue(previous, key, value));
        publish3DFormField(key, value);
    }, [applyFormFieldValue, publish3DFormField]);

    const handleSelectMaterialVariant = useCallback((materialKey) => {
        setSaveStatus(null);
        publish3DViewField('activeMaterialKey', String(materialKey || ''));
        setForm((prev) => {
            const syncedVariants = syncActiveVariant(prev);
            const nextVariant = syncedVariants.find((variant) => variant.materialKey === materialKey);
            if (!nextVariant) return prev;

            const sharedProductPricing = {
                ...keepCurrentSharedDimensions(
                    nextVariant.productPricing,
                    prev.productPricing
                ),
                sizeRules: mergeSharedProductSizeRules(
                    nextVariant.productPricing?.sizeRules,
                    prev.productPricing?.sizeRules || []
                ),
            };
            const canonicalMillingPricing = resolveCanonicalMillingPricing({
                ...prev,
                materialPricingVariants: syncedVariants,
            });
            const selectedVariant = {
                ...nextVariant,
                productPricing: sharedProductPricing,
                millingPricing: {
                    ...keepCurrentSharedDimensions(
                        canonicalMillingPricing,
                        prev.millingPricing,
                        { includeAxisPreset: true }
                    ),
                    ...syncWorkPricingToSharedSizes(
                        canonicalMillingPricing,
                        sharedProductPricing.sizeRules,
                        sharedProductPricing.baseDimensions
                    ),
                },
                textureBinding: normalizeVariantTextureBinding(
                    nextVariant.textureBinding
                ),
            };
            const nextVariants = syncedVariants.map((variant) => (
                variant.materialKey === materialKey ? selectedVariant : variant
            ));

            return {
                ...prev,
                materialPricingVariants: nextVariants,
                activeMaterialKey: materialKey,
                productPricing: selectedVariant.productPricing,
                millingPricing: selectedVariant.millingPricing,
            };
        });
    }, [publish3DViewField, syncActiveVariant]);

    const handleAddMaterialVariant = useCallback((materialKey) => {
        const catalog = blankPriceCatalogs.find((item) => item.key === materialKey);
        if (!catalog) return;
        setSaveStatus(null);
        publish3DViewField('activeMaterialKey', String(materialKey || ''));
        setForm((prev) => {
            const syncedVariants = syncActiveVariant(prev);
            const existing = syncedVariants.find((variant) => variant.materialKey === materialKey);
            if (existing) {
                return {
                    ...prev,
                    materialPricingVariants: syncedVariants,
                    activeMaterialKey: materialKey,
                    productPricing: existing.productPricing,
                    millingPricing: existing.millingPricing,
                };
            }
            const variant = createIndependentMaterialVariant({
                materialKey,
                catalog,
                sourceForm: prev,
            });
            return {
                ...prev,
                materialPricingVariants: [...syncedVariants, variant],
                activeMaterialKey: materialKey,
                productPricing: variant.productPricing,
                millingPricing: variant.millingPricing,
            };
        });
    }, [blankPriceCatalogs, publish3DViewField, syncActiveVariant]);

    const handleQuickSelectMaterialVariant = useCallback((materialKey) => {
        const exists = (Array.isArray(form?.materialPricingVariants) ? form.materialPricingVariants : [])
            .some((variant) => variant?.materialKey === materialKey);

        if (exists) {
            handleSelectMaterialVariant(materialKey);
            return;
        }

        handleAddMaterialVariant(materialKey);
    }, [form?.materialPricingVariants, handleAddMaterialVariant, handleSelectMaterialVariant]);

    const handleSetDefaultMaterialVariant = useCallback((materialKey) => {
        setSaveStatus(null);
        setForm((prev) => ({
            ...prev,
            defaultMaterialKey: materialKey,
            materialPricingVariants: syncActiveVariant(prev).map((variant) => ({
                ...variant,
                isDefault: variant.materialKey === materialKey,
            })),
        }));
    }, [syncActiveVariant]);

    const handleRemoveMaterialVariant = useCallback((materialKey) => {
        setSaveStatus(null);
        setForm((prev) => {
            const syncedVariants = syncActiveVariant(prev);
            if (syncedVariants.length <= 1) return prev;
            const remaining = syncedVariants.filter((variant) => variant.materialKey !== materialKey);
            const nextDefaultKey = prev.defaultMaterialKey === materialKey
                ? (remaining.find((variant) => variant.materialKey === 'gabbro-diabase')?.materialKey || remaining[0].materialKey)
                : prev.defaultMaterialKey;
            const normalized = remaining.map((variant) => ({
                ...variant,
                isDefault: variant.materialKey === nextDefaultKey,
            }));
            const nextVariant = normalized.find((variant) => variant.materialKey === nextDefaultKey) || normalized[0];
            return {
                ...prev,
                defaultMaterialKey: nextDefaultKey,
                activeMaterialKey: nextVariant.materialKey,
                materialPricingVariants: normalized,
                productPricing: nextVariant.productPricing,
                millingPricing: nextVariant.millingPricing,
            };
        });
    }, [syncActiveVariant]);

    const handleAddTagPreset = (values) => {
        setSaveStatus(null);

        setForm((prev) => ({
            ...prev,
            hashtags: mergeTags(prev.hashtags, values),
        }));
    };

    const handleSceneSettingsChange = useCallback((patch = {}) => {
        const safePatch = (
            patch
            && typeof patch === 'object'
            && !Array.isArray(patch)
        ) ? patch : {};

        setSceneSettings((previous) => (
            normalizeAdminProduct3DSceneSettings({
                ...previous,
                ...safePatch,
            })
        ));

        Object.entries(safePatch).forEach(([key, value]) => {
            publish3DViewField(key, value);
        });
    }, [publish3DViewField]);

    const handleSceneSettingChange = useCallback((key, value) => {
        handleSceneSettingsChange({
            [key]: value,
        });
    }, [handleSceneSettingsChange]);

    const changeViewMode = useCallback((nextMode) => {
        const safeMode = ['light', 'studio', 'dark'].includes(nextMode)
            ? nextMode
            : 'light';

        setViewMode(safeMode);
        handleSceneSettingsChange({
            backgroundTheme: safeMode,
        });
        publish3DViewField('viewerMode', safeMode);
    }, [
        handleSceneSettingsChange,
        publish3DViewField,
    ]);

    const changeShowDimensions = useCallback((nextValue) => {
        setShowDimensions(nextValue);
        publish3DViewField('showDimensions', nextValue);
    }, [publish3DViewField]);

    const changeShowBlankDimensions = useCallback((nextValue) => {
        const safeValue = !!nextValue;

        setShowBlankDimensions(safeValue);
        publish3DViewField('showBlankDimensions', safeValue);
    }, [publish3DViewField]);

    const changeShowBlankFrame = useCallback((nextValue) => {
        const safeValue = !!nextValue;

        setShowBlankFrame(safeValue);
        publish3DViewField('showBlankFrame', safeValue);
    }, [publish3DViewField]);

    const changeStageFullscreen = useCallback((nextValue) => {
        const safeValue = !!nextValue;
        setStageFullscreen(safeValue);
        publish3DViewField('stageFullscreen', safeValue);
    }, [publish3DViewField]);

    const changeDisplayControlsOpen = useCallback((nextValue) => {
        const safeValue = !!nextValue;
        setDisplayControlsOpen(safeValue);
        publish3DViewField('displayControlsOpen', safeValue);
    }, [publish3DViewField]);

    const handleSceneToolPanelsChange = useCallback((nextPanels = {}) => {
        const next = {
            background: nextPanels.background === true,
            lighting: nextPanels.lighting === true,
        };
        setSceneToolPanels(next);
        publish3DViewField('sceneBackgroundPanelOpen', next.background);
        publish3DViewField('sceneLightingPanelOpen', next.lighting);
    }, [publish3DViewField]);

    const changePriceModeTab = useCallback((nextTab) => {
        const safeTab = String(nextTab || '') === 'milling' ? 'milling' : 'product';
        setPriceModeTab(safeTab);
        publish3DViewField('priceModeTab', safeTab);
    }, [publish3DViewField]);

    const changeEditorTab = useCallback((nextTab) => {
        const safeTab = ['granite', 'prices', 'tags', 'main', 'settings'].includes(String(nextTab || ''))
            ? String(nextTab)
            : 'prices';

        setEditorTab(safeTab);

        /*
         * CatalogReview уже имеет собственный domain sharedView. Не добавляем
         * туда новый параллельный ключ и не трогаем рабочую прошлую страницу.
         * На ProductSeo/standalone 3D верхняя вкладка идёт semantic-state.
         */
        if (!hasDomainSharedView) {
            publish3DViewField('editorTab', safeTab);
        }
    }, [hasDomainSharedView, publish3DViewField]);

    useEffect(() => {
        if (!isOpen || hasDomainSharedView) return undefined;

        return subscribeAdminRealtimeSemanticState({
            namespace: semantic3DNamespace,
            onState: ({ stateKey, value }) => {
                if (stateKey === 'viewerMode' && ['light', 'studio', 'dark'].includes(String(value))) {
                    setViewMode(String(value));
                    return;
                }
                if (stateKey === 'showDimensions') { setShowDimensions(value === true); return; }
                if (stateKey === 'showBlankDimensions') { setShowBlankDimensions(value === true); return; }
                if (stateKey === 'showBlankFrame') { setShowBlankFrame(value === true); return; }
                if (stateKey === 'stageFullscreen') { setStageFullscreen(value === true); return; }
                if (stateKey === 'displayControlsOpen') { setDisplayControlsOpen(value === true); return; }
                if (stateKey === 'sceneBackgroundPanelOpen') {
                    setSceneToolPanels((previous) => ({ ...previous, background: value === true }));
                    return;
                }
                if (stateKey === 'sceneLightingPanelOpen') {
                    setSceneToolPanels((previous) => ({ ...previous, lighting: value === true }));
                    return;
                }
                if (stateKey === 'activeMaterialKey' && typeof value === 'string') {
                    setForm((previous) => applyActiveMaterialSelectionToForm(previous, value));
                    return;
                }
                if (stateKey === 'priceModeTab') {
                    setPriceModeTab(
                        String(value) === 'milling' || String(value) === 'work'
                            ? 'milling'
                            : 'product'
                    );
                    return;
                }
                if (stateKey === 'editorTab') {
                    const nextTab = String(value || '');
                    if (['granite', 'prices', 'tags', 'main', 'settings'].includes(nextTab)) {
                        setEditorTab(nextTab);
                    }
                    return;
                }
                if (stateKey.startsWith('form.')) {
                    const key = stateKey.slice(5);
                    if (!key) return;

                    setSaveStatus(null);
                    setForm((previous) => applyFormFieldValue(previous, key, value));
                    return;
                }

                const sceneKeys = new Set([
                    'sunAngle', 'sunVertical', 'sunIntensity', 'fillIntensity',
                    'hemisphereIntensity', 'ambientIntensity', 'rimIntensity',
                    'shadowsEnabled', 'graphicsQuality', 'dimensionLabelScale',
                    'backgroundMode', 'backgroundTheme', 'backgroundUrl',
                    'backgroundAsEnvironment', 'showGround',
                ]);
                if (sceneKeys.has(stateKey)) {
                    setSceneSettings((previous) => normalizeAdminProduct3DSceneSettings({
                        ...previous,
                        [stateKey]: value,
                    }));
                }
            },
        });
    }, [
        applyFormFieldValue,
        hasDomainSharedView,
        isOpen,
        semantic3DNamespace,
    ]);

    const refreshProducts = async () => {
        try {
            await Promise.resolve(dispatch(getProducts('createdAtMinus', 0, 2000)));
        } catch (error) {
            return null;
        }

        return null;
    };

    const saveCurrentProduct = async ({ silent = false } = {}) => {
        if (!localProduct?._id) return null;

        const validationErrors = validateAdminProduct3DForm(form, localProduct, { blankPriceCatalogs, pricingPolicy });

        if (validationErrors.length) {
            const visibleErrors = validationErrors.slice(0, 3);
            const hiddenCount = Math.max(0, validationErrors.length - visibleErrors.length);
            const message = `${visibleErrors.join(' ')}${hiddenCount ? ` Ещё ошибок: ${hiddenCount}.` : ''}`;

            setSaveStatus({
                type: 'error',
                text: message,
                savedAt: Date.now(),
            });

            if (silent) {
                const error = new Error(message);
                error.code = 'ADMIN_PRODUCT_3D_FORM_INVALID';
                throw error;
            }

            return null;
        }

        const payload = buildPayload(form, localProduct, { blankPriceCatalogs, pricingPolicy });

        try {
            setSaving(true);

            if (!silent) {
                setSaveStatus(null);
            }

            const res = await updatedProduct(localProduct._id, payload);

            const updated = res?.data?.updatedProduct || {
                ...localProduct,
                ...payload,
            };

            const normalizedUpdated = {
                ...localProduct,
                ...updated,
                hashtags: Array.isArray(updated.hashtags)
                    ? updated.hashtags
                    : payload.hashtags,
                pricingMode: updated.pricingMode || payload.pricingMode,
                workType: updated.workType || payload.workType,
                workLabel: updated.workLabel || payload.workLabel,
                workCalcMode: updated.workCalcMode || payload.workCalcMode,
                workPrice: updated.workPrice ?? payload.workPrice,
                workPricePerMeter: updated.workPricePerMeter ?? payload.workPricePerMeter,
                workPricePerSquareMeter: updated.workPricePerSquareMeter ?? payload.workPricePerSquareMeter,
                workPricePerCubicMeter: updated.workPricePerCubicMeter ?? payload.workPricePerCubicMeter,
                productPricing: updated.productPricing || payload.productPricing,
                materialSelectionMode: updated.materialSelectionMode || payload.materialSelectionMode,
                defaultMaterialKey: updated.defaultMaterialKey || payload.defaultMaterialKey,
                materialPricingVariants: Array.isArray(updated.materialPricingVariants)
                    ? updated.materialPricingVariants
                    : payload.materialPricingVariants,
                millingPricing: updated.millingPricing || payload.millingPricing,
                workOptions: Array.isArray(updated.workOptions) ? updated.workOptions : payload.workOptions,
                modelPartsEnabled: updated.modelPartsEnabled === true,
            };

            const nextCleanForm = getInitialForm(normalizedUpdated, { blankPriceCatalogs });

            setSavedProduct(normalizedUpdated);
            setForm(nextCleanForm);
            setCleanForm(nextCleanForm);

            if (typeof onProductUpdated === 'function') {
                onProductUpdated(normalizedUpdated);
            }

            if (!silent) {
                setSaveStatus({
                    type: 'success',
                    text: res?.data?.message || 'Цена, размеры и теги успешно сохранены',
                    savedAt: Date.now(),
                });
            }

            refreshProducts();

            return normalizedUpdated;
        } catch (error) {
            const message = error?.response?.data?.message || 'Ошибка сохранения товара';

            setSaveStatus({
                type: 'error',
                text: message,
                savedAt: Date.now(),
            });

            throw error;
        } finally {
            setSaving(false);
        }
    };

    const runAction = useCallback((action) => {
        if (!action) return;

        if (action.type === 'close') {
            onClose?.();
            return;
        }

        if (action.type === 'select' && action.product?._id) {
            setLoadError('');
            onSelectProduct?.(action.product);
            return;
        }

        if (action.type === 'category') {
            setNavCategoryId(action.categoryId);

            if (action.product?._id) {
                setLoadError('');
                onSelectProduct?.(action.product);
            }
        }
    }, [onClose, onSelectProduct]);

    const runOrConfirm = useCallback((action) => {
        if (isDirty) {
            setPendingAction(action);
            return;
        }

        runAction(action);
    }, [isDirty, runAction]);

    const selectByIndex = useCallback((index) => {
        if (!navigationProducts.length) return;

        const safeIndex =
            index < 0
                ? navigationProducts.length - 1
                : index >= navigationProducts.length
                    ? 0
                    : index;

        const nextProduct = navigationProducts[safeIndex];

        if (nextProduct?._id) {
            runOrConfirm({
                type: 'select',
                product: nextProduct,
            });
        }
    }, [navigationProducts, runOrConfirm]);

    const handlePrev = useCallback(() => {
        selectByIndex(
            activeIndex <= 0
                ? navigationProducts.length - 1
                : activeIndex - 1
        );
    }, [activeIndex, navigationProducts.length, selectByIndex]);

    const handleNext = useCallback(() => {
        selectByIndex(
            activeIndex >= navigationProducts.length - 1
                ? 0
                : activeIndex + 1
        );
    }, [activeIndex, navigationProducts.length, selectByIndex]);

    const handleChangeCategory = useCallback((event) => {
        const nextCategoryId = event.target.value;

        const source = Array.isArray(products)
            ? products.filter((item) => item?._id && item?.modelPath)
            : [];

        const nextList = nextCategoryId
            ? source.filter((item) => {
                return String(getProductCategoryValue(item)) === String(nextCategoryId);
            })
            : source;

        runOrConfirm({
            type: 'category',
            categoryId: nextCategoryId,
            product: nextList[0] || null,
        });
    }, [products, runOrConfirm]);

    const requestClose = () => {
        if (isViewerMode) {
            onClose?.();
            return;
        }

        runOrConfirm({ type: 'close' });
    };

    const handleConfirmDiscard = () => {
        const action = pendingAction;

        setPendingAction(null);
        setForm(cleanForm);
        runAction(action);
    };

    const handleConfirmSave = async () => {
        const action = pendingAction;

        try {
            await saveCurrentProduct({ silent: true });
            setPendingAction(null);
            runAction(action);
        } catch (error) {
            setPendingAction(null);
        }
    };

    const handleConfirmCancel = () => {
        setPendingAction(null);
    };

    if (!isOpen || !product) return null;

    return (
        <div
            data-catalog-sync-scope={`admin-3d-${catalogNumber || productId || 'unknown'}`}
            className={[
                'adminProduct3DPreview',
                `adminProduct3DPreview--${viewMode}`,
                isViewerMode ? 'adminProduct3DPreview--viewer' : '',
                embedded ? 'adminProduct3DPreview--embedded' : '',
            ].filter(Boolean).join(' ')}
        >
            <section
                className="adminProduct3DPreview__panel"
                data-catalog-scroll-id={`admin-3d-panel-${catalogNumber || productId || 'unknown'}`}
            >
                <header className="adminProduct3DPreview__header">
                    <div className="adminProduct3DPreview__titleBlock">
                        <div className="adminProduct3DPreview__kicker">
                            <ThreeDRotationIcon fontSize="small" />
                            {viewerTitle || (isViewerMode ? '3D-просмотр формы' : '3D-просмотр товара')}
                            {!isViewerMode && isDirty ? (
                                <span className="adminProduct3DPreview__dirtyBadge">
                                    Есть изменения
                                </span>
                            ) : null}
                        </div>

                        <div className="adminProduct3DPreview__nameLine">
                            <span>Название</span>
                            <strong>{getProductName(localProduct)}</strong>
                        </div>

                        <div className="adminProduct3DPreview__sub">
                            Артикул: {localProduct.article || '—'} · Категория:{' '}
                            {localProduct.categoryName || localProduct.categorySlug || '—'}
                        </div>
                    </div>

                    <div className="adminProduct3DPreview__nav">
                        {!embedded ? (
                            <>
                                <select
                                    value={navCategoryId}
                                    data-catalog-sync-key={`admin-3d-nav-category-${catalogNumber || productId || 'unknown'}`}
                                    onChange={handleChangeCategory}
                                >
                                    <option value="">Все товары с 3D</option>

                                    {categoriesForSelect.map((category) => (
                                        <option key={category.value} value={category.value}>
                                            {category.label}
                                        </option>
                                    ))}
                                </select>

                                <div className="adminProduct3DPreview__pager">
                                    <button
                                        type="button"
                                        data-catalog-sync-key={`admin-3d-nav-prev-${catalogNumber || productId || 'unknown'}`}
                                        onClick={handlePrev}
                                        disabled={!canNavigate || saving}
                                    >
                                        <ChevronLeftIcon fontSize="small" />
                                    </button>

                                    <span>
                                        {activeIndex >= 0 ? activeIndex + 1 : 0}
                                        {' / '}
                                        {navigationProducts.length || 0}
                                    </span>

                                    <button
                                        type="button"
                                        data-catalog-sync-key={`admin-3d-nav-next-${catalogNumber || productId || 'unknown'}`}
                                        onClick={handleNext}
                                        disabled={!canNavigate || saving}
                                    >
                                        <ChevronRightIcon fontSize="small" />
                                    </button>
                                </div>
                            </>
                        ) : null}

                    </div>

                    {embedded && typeof onBack === 'function' ? (
                        <button
                            type="button"
                            className="adminProduct3DPreview__modeSwitch"
                            data-catalog-sync-key={`admin-3d-mode-switch-${catalogNumber || productId || 'unknown'}`}
                            onMouseDown={(event) => event.stopPropagation()}
                            onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                onBack();
                            }}
                        >
                            {backLabel}
                        </button>
                    ) : null}

                    <button
                        type="button"
                        className="adminProduct3DPreview__close"
                        data-catalog-sync-key={`admin-3d-close-${catalogNumber || productId || 'unknown'}`}
                        onClick={requestClose}
                        aria-label="Закрыть 3D-просмотр"
                    >
                        <CloseIcon fontSize="small" />
                    </button>
                </header>

                <div
                    className={`adminProduct3DPreview__body ${isViewerMode ? 'is-viewer' : 'is-editor'}`}
                    data-catalog-scroll-id={`admin-3d-body-${catalogNumber || productId || 'unknown'}`}
                >
                    <div className="adminProduct3DPreview__viewport">
                        <AdminProduct3DStage
                            stageKey={`${catalogNumber || productId || 'unknown'}`}
                            modelData={modelData}
                            hasModel={hasModel}
                            loading={effectiveIsLoading}
                            loadingPercent={effectivePercent}
                            loadError={loadError}
                            viewMode={viewMode}
                            onViewModeChange={changeViewMode}
                            dimensions={sceneDimensions}
                            blankDimensions={blankSceneDimensions}
                            dimensionMode={
                                showDimensions
                                    ? (showBlankDimensions && blankSceneDimensions ? 'both' : 'model')
                                    : (showBlankDimensions && blankSceneDimensions ? 'blank' : 'none')
                            }
                            showDimensions={Boolean(
                                showDimensions
                                || (showBlankDimensions && blankSceneDimensions)
                            )}
                            onShowDimensionsChange={changeShowDimensions}
                            showBlankDimensions={showBlankDimensions}
                            onShowBlankDimensionsChange={changeShowBlankDimensions}
                            sceneSettings={sceneSettings}
                            onSceneSettingChange={handleSceneSettingChange}
                            onSceneSettingsChange={handleSceneSettingsChange}
                            sceneToolPanels={sceneToolPanels}
                            onSceneToolPanelsChange={handleSceneToolPanelsChange}
                            externalCameraStateRef={viewportCameraStateRef}
                            showBlankFrame={showBlankFrame}
                            onShowBlankFrameChange={changeShowBlankFrame}
                            realtimeCameraKey={realtimeCamera?.viewportKey || ''}
                            remoteCameraState={realtimeCamera?.remoteState || null}
                            onRealtimeCameraChange={realtimeCamera?.onChange || null}
                            materialTexturePreset={usesGraniteMaterialSelection && activeMaterialCatalog?.texturePreset ? {
                                ...activeMaterialCatalog.texturePreset,
                                materialKey: String(activeMaterialCatalog.key || ''),
                                catalogRevision: Number(activeMaterialCatalog.revision || 0),
                            } : null}
                            materialTextureBinding={usesGraniteMaterialSelection ? (activeMaterialVariant?.textureBinding || null) : null}
                            unpolishedSurfaceTexturePreset={usesGraniteMaterialSelection && unpolishedSurfaceCatalog?.texturePreset ? {
                                ...unpolishedSurfaceCatalog.texturePreset,
                                catalogRevision: Number(unpolishedSurfaceCatalog.revision || 0),
                            } : null}
                            surfaceFinishPolicy={usesGraniteMaterialSelection ? (form?.surfaceFinishPolicy || null) : null}
                            fullscreen={stageFullscreen}
                            onFullscreenChange={changeStageFullscreen}
                            displayControlsOpen={displayControlsOpen}
                            onDisplayControlsOpenChange={changeDisplayControlsOpen}
                            graniteControls={usesGraniteMaterialSelection && blankPriceCatalogs.length ? (
                                <AdminGraniteTypeSwitcher
                                    mode="rail"
                                    catalogs={blankPriceCatalogs}
                                    variants={Array.isArray(form?.materialPricingVariants) ? form.materialPricingVariants : []}
                                    activeMaterialKey={form?.activeMaterialKey || form?.defaultMaterialKey || 'gabbro-diabase'}
                                    defaultMaterialKey={form?.defaultMaterialKey || 'gabbro-diabase'}
                                    commonDimensions={graniteCommonDimensions}
                                    priceSummaryByKey={granitePriceSummaryByKey}
                                    priceDisplayMode={priceModeTab}
                                    saving={saving}
                                    onSelectVariant={handleQuickSelectMaterialVariant}
                                />
                            ) : null}
                        />
                    </div>

                    {isViewerMode && !embedded ? (
                        <aside
                            className="adminProduct3DPreview__viewerInfo"
                            data-catalog-scroll-id={`admin-3d-info-${catalogNumber || productId || 'unknown'}`}
                        >
                            <div className="adminProduct3DPreview__viewerDimensionCards">
                                <div className="adminProduct3DPreview__viewerInfoCard is-model">
                                    <span>Размер в сцене</span>
                                    <strong>
                                        {formatViewerDimensions(requestedModelDimensions)}
                                    </strong>
                                    <small>
                                        Объект и размерные линии используют один выбранный размер.
                                    </small>
                                </div>
                            </div>

                            <div className="adminProduct3DPreview__viewerInfoGrid">
                                <div>
                                    <span>Файл модели</span>
                                    <strong>{localProduct.modelPath ? 'Подключён' : 'Не указан'}</strong>
                                </div>
                                <div>
                                    <span>Подписка</span>
                                    <strong>{fmtMoney(activeGranitePriceSummary.subscriberPrice)}</strong>
                                </div>
                                <div>
                                    <span>Обычная</span>
                                    <strong>{fmtMoney(activeGranitePriceSummary.publicPrice)}</strong>
                                </div>
                                <div>
                                    <span>Теги</span>
                                    <strong>{Array.isArray(localProduct.hashtags) ? localProduct.hashtags.length : 0}</strong>
                                </div>
                            </div>

                            {Array.isArray(localProduct.hashtags) && localProduct.hashtags.length ? (
                                <div className="adminProduct3DPreview__viewerTags">
                                    {localProduct.hashtags.slice(0, 40).map((tag) => (
                                        <span key={tag}>{tag}</span>
                                    ))}
                                </div>
                            ) : (
                                <div className="adminProduct3DPreview__viewerEmpty">У товара пока нет тегов.</div>
                            )}
                        </aside>
                    ) : (
                        <AdminProduct3DInfoEditor
                            product={localProduct}
                            form={form}
                            saving={saving}
                            saveStatus={saveStatus}
                            isDirty={isDirty}
                            initialForm={cleanForm}
                            realDimensions={realDimensions}
                            sceneDimensions={sceneDimensions}
                            pricingDimensions={graniteCommonDimensions}
                            showDimensions={showDimensions}
                            sceneSettings={sceneSettings}
                            blankPriceCatalogs={blankPriceCatalogs}
                            pricingPolicy={pricingPolicy}
                            modelMaterials={Array.isArray(modelData?.materials) ? modelData.materials : (modelData?.materials ? [modelData.materials] : [])}
                            activeEditorTab={editorTab}
                            onEditorTabChange={changeEditorTab}
                            priceModeTab={priceModeTab}
                            onPriceModeTabChange={changePriceModeTab}
                            onSelectMaterialVariant={handleSelectMaterialVariant}
                            onAddMaterialVariant={handleAddMaterialVariant}
                            onSetDefaultMaterialVariant={handleSetDefaultMaterialVariant}
                            onRemoveMaterialVariant={handleRemoveMaterialVariant}
                            onToggleDimensions={changeShowDimensions}
                            onSceneSettingChange={handleSceneSettingChange}
                            onChange={handleChange}
                            onAddTagPreset={handleAddTagPreset}
                            onReset={setForm}
                            onSave={saveCurrentProduct}
                        />
                    )}
                </div>
            </section>

            {!isViewerMode && pendingAction ? (
                <div className="adminProduct3DPreview__confirmOverlay">
                    <div className="adminProduct3DPreview__confirmBox">
                        <div className="adminProduct3DPreview__confirmIcon">
                            <WarningAmberIcon />
                        </div>

                        <strong>{getConfirmTitle(pendingAction)}</strong>

                        <p>{getConfirmText(pendingAction)}</p>

                        {changeSummary.length ? (
                            <div className="adminProduct3DPreview__confirmChanges">
                                <b>Что изменено:</b>

                                <div>
                                    {changeSummary.map((item, index) => (
                                        <section key={`${item.label}-${index}`}>
                                            <span>{item.label}</span>
                                            <p>
                                                <em>Было:</em> {item.before}
                                            </p>
                                            <p>
                                                <em>Стало:</em> {item.after}
                                            </p>
                                        </section>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div className="adminProduct3DPreview__confirmChanges">
                                <b>Изменения не найдены</b>
                                <div>
                                    <section>
                                        <span>Состояние</span>
                                        <p>Форма выглядит сохранённой. Можно переходить дальше.</p>
                                    </section>
                                </div>
                            </div>
                        )}

                        <div className="adminProduct3DPreview__confirmActions">
                            <button
                                type="button"
                                className="adminProduct3DPreview__dangerBtn"
                                data-catalog-sync-key={`admin-3d-confirm-discard-${catalogNumber || productId || 'unknown'}`}
                                onClick={handleConfirmDiscard}
                                disabled={saving}
                            >
                                {getDiscardButtonText(pendingAction)}
                            </button>

                            <button
                                type="button"
                                className="adminProduct3DPreview__secondaryBtn"
                                data-catalog-sync-key={`admin-3d-confirm-stay-${catalogNumber || productId || 'unknown'}`}
                                onClick={handleConfirmCancel}
                                disabled={saving}
                            >
                                Остаться здесь
                            </button>

                            <button
                                type="button"
                                className="adminProduct3DPreview__primaryBtn"
                                data-catalog-sync-key={`admin-3d-confirm-save-${catalogNumber || productId || 'unknown'}`}
                                onClick={handleConfirmSave}
                                disabled={saving}
                            >
                                <SaveIcon fontSize="small" />
                                {getSaveButtonText(pendingAction, saving)}
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default AdminProduct3DPreview;
